import { io, Socket } from 'socket.io-client'
import type { ServerToClientEvents, ClientToServerEvents } from '@territory-nexus/shared'
import { supabase } from './supabase'

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:4000'

let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null
let refreshing = false
let pendingLocation: { lat: number; lng: number } | null = null

export const initializeSocket = (token: string) => {
  // If already connected with same token, return existing socket
  if (socket?.connected) {
    return socket
  }

  // Disconnect existing socket if any
  if (socket) {
    socket.disconnect()
  }

  socket = io(WS_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
    timeout: 10000,
  })

  socket.on('connect', () => {
    console.log('Socket connected:', socket?.id)
    // Send any buffered location update that arrived before connect
    if (pendingLocation && socket?.connected) {
      socket.emit('location:update', { lat: pendingLocation.lat, lng: pendingLocation.lng, accuracy: 0, timestamp: new Date() })
      pendingLocation = null
    }
  })

  socket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason)
    // If the server closed the connection, socket.io won't auto-reconnect for these reasons
    if (reason === 'io server disconnect') {
      // Server forced disconnect (e.g. auth failure) — reconnect with fresh token
      refreshAndReconnect()
    }
  })

  // Handle connection errors (e.g. expired token rejected by server auth middleware)
  socket.on('connect_error', async (err) => {
    console.error('Socket connect_error:', err.message)
    // If auth failed, refresh token and retry
    if (err.message.includes('Authentication') || err.message.includes('Invalid') || err.message.includes('token')) {
      await refreshAndReconnect()
    }
  })

  socket.on('error', (error) => {
    console.error('Socket error:', error)
  })

  return socket
}

async function refreshAndReconnect() {
  if (!socket || refreshing) return
  refreshing = true
  try {
    // Actually refresh the token, not just read the (possibly stale) cached session
    const { data: { session }, error } = await supabase.auth.refreshSession()
    if (error || !session?.access_token) {
      console.error('Socket token refresh failed:', error?.message)
      return
    }
    socket.auth = { token: session.access_token }
    socket.connect()
  } catch (err) {
    console.error('Failed to refresh socket token:', err)
  } finally {
    refreshing = false
  }
}

export const updateSocketToken = (token: string) => {
  if (socket) {
    socket.auth = { token }
    // If disconnected, reconnect with new token
    if (!socket.connected) {
      socket.connect()
    }
  }
}

export const getSocket = () => socket

export const isSocketConnected = () => socket?.connected ?? false

export const sendLocationUpdate = (lat: number, lng: number) => {
  if (socket?.connected) {
    socket.emit('location:update', { lat, lng, accuracy: 0, timestamp: new Date() })
  } else {
    // Buffer so it gets sent when socket connects
    pendingLocation = { lat, lng }
  }
}

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}
